import { RefCallback, ReactElement } from 'react';
import { useDrop, DropTargetMonitor } from 'react-dnd';
import { TargetType } from 'dnd-core';

import { DraggableItem } from '../Draggable';

export interface DroppableProps<T extends object = object> {
  /** Type of item allowed to be dropped on this drop target */
  accept: TargetType;
  /** Called when drop target is hovered with valid Droppable item */
  onHover?: (item: DraggableItem<T>, monitor: DropTargetMonitor) => void;
  /** Called when drag item is dropped on a valid drop target */
  onDrop?: (item: DraggableItem<T>, monitor: DropTargetMonitor) => void;
  /** Called when item is dropped on this drop target */
  children: (refs: {
    dropRef: RefCallback<HTMLElement>;
    collected: { canDrop: boolean; isOver: boolean };
  }) => ReactElement;
}

const Droppable = <T extends object = object>({
  accept,
  onDrop,
  onHover,
  children
}: DroppableProps<T>) => {
  const [collected, dropRef] = useDrop(
    () => ({
      accept,
      drop: onDrop,
      hover: onHover,
      collect: monitor => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver()
      })
    }),
    [accept, onDrop, onHover]
  );

  return children({ dropRef, collected });
};

export default Droppable;
